import React from "react";
import { Avatar } from "@/components/Avatar";
import { Badge } from "@/components/ui/badge";

/**
 * Renders a talent's anonymized Marketplace card.
 *
 * Shared by:
 *   • Talent Passport "Preview as employer" modal
 *   • Public share link page (/preview/:token)
 *   • Public /how-it-works marketing page (with a demo card)
 *
 * Never receives identity fields (name, email, phone, resume text).
 * Server-side ``_passport_to_marketplace_card()`` guarantees that.
 */
export const AnonymizedCard = ({ card, testId = "anonymized-card" }) => {
    if (!card) return null;
    return (
        <div className="card-soft p-4" data-testid={testId}>
            <div className="flex items-start gap-3 min-w-0">
                <Avatar
                    url={null}
                    initials={card.initials}
                    userId={card.id}
                    size={44}
                    ring={false}
                />
                <div className="min-w-0 flex-1">
                    <div className="font-heading font-semibold text-foreground truncate">
                        {card.desired_position || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5 flex flex-wrap gap-x-2">
                        {card.city && <span>{card.city}</span>}
                        {card.years_experience && <span>· {card.years_experience} yrs</span>}
                        {card.availability && <span>· {card.availability}</span>}
                        {card.remote_preferences && <span>· {card.remote_preferences}</span>}
                    </div>
                    {card.skills?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {card.skills.slice(0, 8).map((s, i) => (
                                <Badge key={i} variant="outline" className="text-[10px]">{s}</Badge>
                            ))}
                        </div>
                    )}
                    {card.work_authorization && (
                        <div className="text-[11px] text-muted-foreground mt-1.5">
                            Work auth: {card.work_authorization}
                        </div>
                    )}
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Verified</div>
                    <div className="mono-score text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {card.trust_score || 0}
                    </div>
                </div>
            </div>
            {card.trust_breakdown?.tiles && (
                <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-1.5">
                    {card.trust_breakdown.tiles.map((t, i) => (
                        <span
                            key={i}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                t.state === "verified"
                                    ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-500/10"
                                    : t.state === "pending"
                                        ? "border-amber-500/40 text-amber-700 dark:text-amber-400 bg-amber-500/10"
                                        : "border-border text-muted-foreground"
                            }`}
                        >
                            {t.label}: {t.state.replace(/_/g, " ")}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AnonymizedCard;
